import { JobPermission } from "projen/lib/github/workflows-model";
import type { NodeProject } from "projen/lib/javascript";

/** Merge methods supported by enable-pull-request-automerge. */
export const MERGE_METHODS = {
  SQUASH: "squash",
  MERGE: "merge",
  REBASE: "rebase",
} as const;

/** Merge method for the approve-and-merge-upgrade workflow. */
export type MergeMethod = (typeof MERGE_METHODS)[keyof typeof MERGE_METHODS];

/**
 * Options for the approve-and-merge-upgrade workflow (ADR 0001 §2).
 * When set, adds a workflow that approves and merges the dependency-upgrade PR
 * when it has the auto-approve label and head branch matches. Trigger types
 * exclude `opened` to prevent double approval when the PR is created with the label.
 */
export interface ApproveMergeUpgradeOptions {
  /**
   * Workflow file name (display name in Actions tab).
   * @default "approve-and-merge-upgrade"
   */
  readonly workflowName?: string;

  /**
   * Label that must be present on the PR to trigger approve-and-merge.
   * @default "auto-approve"
   */
  readonly autoApproveLabel?: string;

  /**
   * Head branch name the PR must target (e.g. the branch created by the upgrade workflow).
   * @default "github-actions/upgrade"
   */
  readonly headBranch?: string;

  /**
   * GitHub usernames allowed as PR authors (e.g. automation bot logins).
   * The workflow runs only when the PR user is in this list.
   */
  readonly allowedUsernames: string[];

  /**
   * Repository secret name for the approval GitHub App ID.
   * @default "APPROVAL_APP_ID"
   */
  readonly approvalAppIdSecret?: string;

  /**
   * Repository secret name for the approval GitHub App private key (PEM).
   * @default "APPROVAL_APP_PRIVATE_KEY"
   */
  readonly approvalAppPrivateKeySecret?: string;

  /**
   * Merge method for enable-pull-request-automerge.
   * @default "squash"
   */
  readonly mergeMethod?: MergeMethod;

  /**
   * Optional branch filters for pull_request_target (e.g. base branches).
   * @see https://docs.github.com/en/actions/using-workflows/events-that-trigger-workflows#pull_request_target
   */
  readonly branches?: string[];
}

const DEFAULT_WORKFLOW_NAME = "approve-and-merge-upgrade";
const DEFAULT_AUTO_APPROVE_LABEL = "auto-approve";
const DEFAULT_HEAD_BRANCH = "github-actions/upgrade";
const DEFAULT_APPROVAL_APP_ID_SECRET = "APPROVAL_APP_ID";
const DEFAULT_APPROVAL_APP_PRIVATE_KEY_SECRET = "APPROVAL_APP_PRIVATE_KEY";
const DEFAULT_MERGE_METHOD = MERGE_METHODS.SQUASH;

/**
 * Trigger types for pull_request_target. Excludes `opened` so that when the
 * upgrade workflow creates a PR with the auto-approve label, GitHub emits both
 * `opened` and `labeled` and we avoid running the workflow twice (double approval).
 */
const PULL_REQUEST_TARGET_TYPES = [
  "labeled",
  "synchronize",
  "reopened",
  "ready_for_review",
] as const;

/**
 * Adds the approve-and-merge-upgrade workflow to the project.
 * Uses a GitHub App token for approve + enable automerge (squash by default).
 *
 * @param project - A NodeProject with GitHub (e.g. MonorepoProject)
 * @param options - Options for the workflow; allowedUsernames is required
 */
export function addApproveMergeUpgradeWorkflow(
  project: NodeProject,
  options: ApproveMergeUpgradeOptions,
): void {
  const workflowName = options.workflowName ?? DEFAULT_WORKFLOW_NAME;
  const autoApproveLabel =
    options.autoApproveLabel ?? DEFAULT_AUTO_APPROVE_LABEL;
  const headBranch = options.headBranch ?? DEFAULT_HEAD_BRANCH;
  const allowedUsernames = options.allowedUsernames;
  const appIdSecret =
    options.approvalAppIdSecret ?? DEFAULT_APPROVAL_APP_ID_SECRET;
  const privateKeySecret =
    options.approvalAppPrivateKeySecret ??
    DEFAULT_APPROVAL_APP_PRIVATE_KEY_SECRET;
  const mergeMethod = options.mergeMethod ?? DEFAULT_MERGE_METHOD;

  const workflow = project.github?.addWorkflow(workflowName);
  if (!workflow) return;

  workflow.on({
    pullRequestTarget: {
      types: [...PULL_REQUEST_TARGET_TYPES],
      ...(options.branches && options.branches.length > 0
        ? { branches: options.branches }
        : {}),
    },
  });

  const jobCondition = [
    `contains(github.event.pull_request.labels.*.name, '${autoApproveLabel}')`,
    `github.event.pull_request.head.ref == '${headBranch}'`,
    `(${allowedUsernames.map((u) => `github.event.pull_request.user.login == '${u}'`).join(" || ")})`,
  ].join(" && ");

  workflow.addJobs({
    "approve-and-merge": {
      name: "Approve and merge upgrade PR",
      runsOn: ["ubuntu-latest"],
      permissions: {
        pullRequests: JobPermission.WRITE,
        contents: JobPermission.WRITE,
      },
      if: jobCondition,
      steps: [
        {
          name: "Generate token",
          id: "generate_token",
          uses: "actions/create-github-app-token@v2",
          with: {
            "app-id": `\${{ secrets.${appIdSecret} }}`,
            "private-key": `\${{ secrets.${privateKeySecret} }}`,
          },
        },
        {
          name: "Auto-approve",
          uses: "hmarr/auto-approve-action@f0939ea97e9205ef24d872e76833fa908a770363",
          with: {
            "github-token": "${{ steps.generate_token.outputs.token }}",
          },
        },
        {
          name: "Enable auto-merge (squash)",
          uses: "peter-evans/enable-pull-request-automerge@v3",
          with: {
            token: "${{ steps.generate_token.outputs.token }}",
            "pull-request-number": "${{ github.event.pull_request.number }}",
            "merge-method": mergeMethod,
          },
        },
      ],
    },
  });
}
